import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, ExternalLink, ImagePlus, Eye } from "lucide-react";
import { buildPublicAppUrl } from "@/lib/appConfig";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { sanitizeRichTextHtml } from "@/lib/richText";

interface BlogPostRecord {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  meta_title: string | null;
  meta_description: string | null;
  cover_image_url: string | null;
  published: boolean;
  published_at: string | null;
  author_name: string | null;
  created_at: string;
  updated_at: string;
}

interface BlogFaqRecord {
  id?: string;
  question: string;
  answer: string;
  sort_order: number;
}

interface BlogFormState {
  id?: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  meta_title: string;
  meta_description: string;
  cover_image_url: string;
  author_name: string;
  published: boolean;
  faqs: BlogFaqRecord[];
}

const EMPTY_FORM: BlogFormState = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  meta_title: "",
  meta_description: "",
  cover_image_url: "",
  author_name: "",
  published: false,
  faqs: [],
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function BlogManager() {
  const [posts, setPosts] = useState<BlogPostRecord[]>([]);
  const [form, setForm] = useState<BlogFormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingInlineImage, setUploadingInlineImage] = useState(false);

  const selectedPost = useMemo(
    () => posts.find((post) => post.id === form.id) ?? null,
    [form.id, posts]
  );

  const loadPosts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("updated_at", { ascending: false });

      if (error) throw error;
      setPosts((data || []) as BlogPostRecord[]);
    } catch (error) {
      console.error("Failed to load blog posts", error);
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPostDetails = async (postId: string) => {
    try {
      const [{ data: post, error: postError }, { data: faqs, error: faqsError }] = await Promise.all([
        supabase.from("blog_posts").select("*").eq("id", postId).single(),
        supabase
          .from("blog_post_faqs")
          .select("id, question, answer, sort_order")
          .eq("blog_post_id", postId)
          .order("sort_order", { ascending: true }),
      ]);

      if (postError) throw postError;
      if (faqsError) throw faqsError;

      setForm({
        id: post.id,
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt || "",
        content: post.content || "",
        meta_title: post.meta_title || "",
        meta_description: post.meta_description || "",
        cover_image_url: post.cover_image_url || "",
        author_name: post.author_name || "",
        published: Boolean(post.published),
        faqs: (faqs || []).map((faq) => ({
          id: faq.id,
          question: faq.question,
          answer: faq.answer,
          sort_order: faq.sort_order || 0,
        })),
      });
    } catch (error) {
      console.error("Failed to load post details", error);
      toast.error("Failed to load post details");
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim() || !form.content.trim()) {
      toast.error("Title, slug, and content are required");
      return;
    }

    setSaving(true);
    try {
      const now = new Date().toISOString();
      const postPayload = {
        title: form.title.trim(),
        slug: slugify(form.slug),
        excerpt: form.excerpt.trim() || null,
        content: sanitizeRichTextHtml(form.content.trim()),
        meta_title: form.meta_title.trim() || null,
        meta_description: form.meta_description.trim() || null,
        cover_image_url: form.cover_image_url.trim() || null,
        author_name: form.author_name.trim() || null,
        published: form.published,
        published_at: form.published ? selectedPost?.published_at || now : null,
        updated_at: now,
      };

      let postId = form.id;

      if (postId) {
        const { error } = await supabase.from("blog_posts").update(postPayload).eq("id", postId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("blog_posts")
          .insert({
            ...postPayload,
            created_at: now,
          })
          .select("id")
          .single();

        if (error) throw error;
        postId = data.id;
      }

      const cleanedFaqs = form.faqs
        .map((faq, index) => ({
          id: faq.id,
          question: faq.question.trim(),
          answer: faq.answer.trim(),
          sort_order: index,
        }))
        .filter((faq) => faq.question && faq.answer);

      const { error: deleteFaqsError } = await supabase
        .from("blog_post_faqs")
        .delete()
        .eq("blog_post_id", postId);

      if (deleteFaqsError) throw deleteFaqsError;

      if (cleanedFaqs.length > 0) {
        const { error: insertFaqsError } = await supabase.from("blog_post_faqs").insert(
          cleanedFaqs.map((faq) => ({
            blog_post_id: postId,
            question: faq.question,
            answer: faq.answer,
            sort_order: faq.sort_order,
          }))
        );

        if (insertFaqsError) throw insertFaqsError;
      }

      toast.success(postId === form.id ? "Blog post updated" : "Blog post created");
      await loadPosts();
      await loadPostDetails(postId);
    } catch (error: any) {
      console.error("Failed to save blog post", error);
      toast.error(error.message || "Failed to save blog post");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (postId: string) => {
    setDeletingId(postId);
    try {
      const { error } = await supabase.from("blog_posts").delete().eq("id", postId);
      if (error) throw error;
      toast.success("Blog post deleted");
      if (form.id === postId) {
        resetForm();
      }
      await loadPosts();
    } catch (error) {
      console.error("Failed to delete blog post", error);
      toast.error("Failed to delete blog post");
    } finally {
      setDeletingId(null);
    }
  };

  const updateFaq = (index: number, field: keyof BlogFaqRecord, value: string | number) => {
    setForm((prev) => ({
      ...prev,
      faqs: prev.faqs.map((faq, faqIndex) =>
        faqIndex === index ? { ...faq, [field]: value } : faq
      ),
    }));
  };

  const uploadBlogImage = async (file: File, mode: "cover" | "inline") => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return "";
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be smaller than 5MB");
      return "";
    }

    const setUploading = mode === "cover" ? setUploadingCover : setUploadingInlineImage;
    setUploading(true);

    try {
      const extension = file.name.split(".").pop() || "jpg";
      const safeSlug = slugify(form.slug || form.title || "blog-post");
      const fileName = `${safeSlug}/${mode}-${Date.now()}.${extension}`;

      const { error: uploadError } = await supabase.storage
        .from("blog-media")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("blog-media").getPublicUrl(fileName);

      if (mode === "cover") {
        setForm((prev) => ({ ...prev, cover_image_url: publicUrl }));
      }

      toast.success(mode === "cover" ? "Cover image uploaded" : "Image uploaded");
      return publicUrl;
    } catch (error) {
      console.error("Failed to upload blog image", error);
      toast.error("Failed to upload image");
      return "";
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Blog Posts</CardTitle>
              <CardDescription>Manage blog content and article FAQs.</CardDescription>
            </div>
            <Button size="sm" variant="outline" onClick={resetForm}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading posts...
            </div>
          ) : posts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No blog posts yet.</p>
          ) : (
            posts.map((post) => (
              <button
                key={post.id}
                type="button"
                onClick={() => loadPostDetails(post.id)}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                  form.id === post.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/40"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium">{post.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">/{post.slug}</p>
                  </div>
                  <Badge variant={post.published ? "default" : "secondary"}>
                    {post.published ? "Published" : "Draft"}
                  </Badge>
                </div>
              </button>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>{form.id ? "Edit Blog Post" : "Create Blog Post"}</CardTitle>
              <CardDescription>
                Add SEO metadata, a clean slug, and FAQs for schema markup.
              </CardDescription>
            </div>
            {form.slug && (
              <Button asChild size="sm" variant="outline">
                <a href={buildPublicAppUrl(`/blog/${slugify(form.slug)}`)} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Preview
                </a>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="blog-title">Title</Label>
              <Input
                id="blog-title"
                value={form.title}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    title: event.target.value,
                    slug: prev.id ? prev.slug : slugify(event.target.value),
                  }))
                }
                placeholder="How Split Payments Increase AOV"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="blog-slug">Slug</Label>
              <Input
                id="blog-slug"
                value={form.slug}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    slug: slugify(event.target.value),
                  }))
                }
                placeholder="how-split-payments-increase-aov"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="blog-author">Author</Label>
              <Input
                id="blog-author"
                value={form.author_name}
                onChange={(event) => setForm((prev) => ({ ...prev, author_name: event.target.value }))}
                placeholder="Splitkart Team"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="blog-excerpt">Excerpt</Label>
              <Textarea
                id="blog-excerpt"
                value={form.excerpt}
                onChange={(event) => setForm((prev) => ({ ...prev, excerpt: event.target.value }))}
                rows={3}
                placeholder="A concise summary for the listing page."
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <RichTextEditor
                label="Content"
                value={form.content}
                onChange={(content) => setForm((prev) => ({ ...prev, content }))}
                placeholder="Write the blog content here with headings, lists, links, and images."
                onImageUpload={(file) => uploadBlogImage(file, "inline")}
                uploadingImage={uploadingInlineImage}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="blog-cover-url">Cover Image URL</Label>
              <div className="flex flex-col gap-3 md:flex-row">
                <Input
                  id="blog-cover-url"
                  value={form.cover_image_url}
                  onChange={(event) => setForm((prev) => ({ ...prev, cover_image_url: event.target.value }))}
                  placeholder="https://..."
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("blog-cover-file-input")?.click()}
                  disabled={uploadingCover}
                >
                  {uploadingCover ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="mr-2 h-4 w-4" />
                  )}
                  Upload
                </Button>
                <input
                  id="blog-cover-file-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (event) => {
                    const file = event.target.files?.[0];
                    event.target.value = "";
                    if (file) {
                      await uploadBlogImage(file, "cover");
                    }
                  }}
                />
              </div>
              {form.cover_image_url && (
                <div className="overflow-hidden rounded-2xl border border-border">
                  <img
                    src={form.cover_image_url}
                    alt={form.title || "Blog cover"}
                    className="max-h-64 w-full object-cover"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="blog-meta-title">Meta Title</Label>
              <Input
                id="blog-meta-title"
                value={form.meta_title}
                onChange={(event) => setForm((prev) => ({ ...prev, meta_title: event.target.value }))}
                placeholder="SEO title for search"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="blog-meta-description">Meta Description</Label>
              <Textarea
                id="blog-meta-description"
                value={form.meta_description}
                onChange={(event) => setForm((prev) => ({ ...prev, meta_description: event.target.value }))}
                rows={3}
                placeholder="SEO description for search snippets"
              />
            </div>

            <div className="flex items-center justify-between rounded-xl border p-4 md:col-span-2">
              <div>
                <p className="font-medium">Published</p>
                <p className="text-sm text-muted-foreground">
                  Published posts show on the public blog page and can be indexed.
                </p>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(checked) => setForm((prev) => ({ ...prev, published: checked }))}
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-semibold">Article FAQs</h3>
                <p className="text-sm text-muted-foreground">These power FAQ schema on the blog detail page.</p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    faqs: [...prev.faqs, { question: "", answer: "", sort_order: prev.faqs.length }],
                  }))
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                Add FAQ
              </Button>
            </div>

            {form.faqs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No FAQs added for this article yet.</p>
            ) : (
              <div className="space-y-4">
                {form.faqs.map((faq, index) => (
                  <div key={`${faq.id || "new"}-${index}`} className="rounded-xl border p-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium">FAQ {index + 1}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            faqs: prev.faqs.filter((_, faqIndex) => faqIndex !== index),
                          }))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div className="space-y-2">
                        <Label>Question</Label>
                        <Input
                          value={faq.question}
                          onChange={(event) => updateFaq(index, "question", event.target.value)}
                          placeholder="What happens if a friend doesn't pay?"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Answer</Label>
                        <Textarea
                          rows={4}
                          value={faq.answer}
                          onChange={(event) => updateFaq(index, "answer", event.target.value)}
                          placeholder="Explain the answer clearly for customers and search engines."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save Post
            </Button>
            {form.id && (
              <Button
                variant="destructive"
                onClick={() => handleDelete(form.id!)}
                disabled={deletingId === form.id}
              >
                {deletingId === form.id ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-2 h-4 w-4" />
                )}
                Delete Post
              </Button>
            )}
            {form.id && (
              <Button variant="outline" onClick={() => loadPostDetails(form.id!)}>
                <Eye className="mr-2 h-4 w-4" />
                Reload
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
